import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { AuthenticationService } from '../../../../core/services/authentication.service';
import { AttendanceStatsPlacement, AvailableWidgets } from '../../../../setting/models/AvailableWidgets';
import { CompanyService } from '../../../../setting/services/company.service';
import { Employee } from '../../../models/Employee';
import { DatetimeType, EmployeeStats } from '../../../models/EmployeeStats';
import { EmployeeService } from '../../../services/employee.service';
import { ChangeEmployeeInCompanyFromModalComponent } from '../../partials/change-employee-in-company-from-modal/change-employee-in-company-from-modal.component';
import { ChangeEmployeePolicyModalComponent } from '../../partials/change-employee-policy-modal/change-employee-policy-modal.component';

@Component({
    selector: 'app-employee-attendance-stats',
    templateUrl: './employee-attendance-stats.component.html',
    styleUrls: ['./employee-attendance-stats.component.css']
})
export class EmployeeAttendanceStatsComponent implements OnInit {
    @Input() public employeeID: number;
    @Input() public placement: AttendanceStatsPlacement = 'DASHBOARD';
    @Output() public fetchEmployeeExtendedFn = new EventEmitter<string>();

    public stats$: Observable<EmployeeStats>;
    public availableWidgets$: Observable<AvailableWidgets>;
    public attendanceCols = 0;
    public canEditCompanyPolicy = false;
    public initDone = false;

    public constructor(
        private _companyService: CompanyService,
        private _employeeService: EmployeeService,
        private _authService: AuthenticationService,
        private _modalService: NgbModal,
    ) { }

    private _employee$: Observable<Employee>;

    public get employee$(): Observable<Employee> {
        return this._employee$;
    }

    @Input() public set employee$(value: Observable<Employee>) {
        this._employee$ = value.pipe(
            tap(employee => {
                if (this.initDone) {
                    this._getEmployeeStats();
                }
                this.initDone = true;
            }),
            shareReplay()
        );
    }

    public ngOnInit(): void {
        this.availableWidgets$ = this._companyService.availableWidgets$;
        this.canEditCompanyPolicy = this._authService.checkPermission('companyEmployeePolicy.edit');
        this._getEmployeeStats();
    }

    public openEmployeeInCompanyFromModal(inCompanyFrom: string, employee: Employee): void {
        const modalRef = this._modalService.open(ChangeEmployeeInCompanyFromModalComponent, {centered: true});

        modalRef.componentInstance.inCompanyFrom = inCompanyFrom;
        modalRef.componentInstance.employee = employee;

        modalRef.result
            .then(
                () => {
                    this._getEmployeeStats();
                    this.fetchEmployeeExtendedFn.emit();
                },
                () => {}
            );
    }

    public openPolicySettingsModal(employee: Employee, stat?: DatetimeType, lastAdjustmentDate?: string): void {
        const modalRef = this._modalService.open(ChangeEmployeePolicyModalComponent, {centered: true});

        modalRef.componentInstance.employee = employee;
        modalRef.componentInstance.selectedStatTypeID = stat?.employee_datetime_type_ID;
        modalRef.componentInstance.effectiveFromMinDate = lastAdjustmentDate;

        modalRef.result
            .then(
                () => {
                    this._getEmployeeStats();
                    this.fetchEmployeeExtendedFn.emit();
                },
                () => {}
            );
    }

    private _getEmployeeStats(): void {
        this.stats$ = combineLatest([
            this._employeeService.getEmployeeStatsByEmployeeID(this.employeeID),
            this.availableWidgets$
        ]).pipe(
            tap(([stats, availableWidgets]) => this._checkForCols(availableWidgets, stats)),
            map(([stats]) => stats),
            shareReplay()
        );
    }

    private _checkForCols(availableWidgets: AvailableWidgets, stats: EmployeeStats): void {
        this.attendanceCols = 0;
        const usedWidgets = availableWidgets.attendance_stats;

        if (usedWidgets.show_days_in_company) {
            this.attendanceCols++;
        }
        if (usedWidgets.show_weekly_stats) {
            this.attendanceCols++;
        }
        if (usedWidgets.show_monthly_stats) {
            this.attendanceCols++;
        }

        this.attendanceCols += stats.employee_datetime_types.length;

        if (this.attendanceCols > 4) {
            this.attendanceCols = 4;
        }
    }
}
